import { create } from "zustand";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../utils/universalAPIErrorHandler";
import {
  ImportSettingsApplyResponse,
  ImportSettingsPreview,
  applyImportSettings,
  previewImportSettings,
} from "../../utils/importSettingsApi";
import { logger } from "../../../utils/logger";

/**
 * Preview/apply state for "import settings from the VMS" (ENG-2461), shared
 * by the post-auto-add card and the ignore-zone editor's on-demand import.
 * One flow runs at a time; `reset()` before starting a new one.
 *
 * Errors follow the Immix Remote Link store: the shared handler gets a retry
 * that re-runs the same call, and only a terminal failure sets `*Error`. A
 * token refresh or scheduled retry leaves the flow "loading" so a subscriber
 * picks the retried result up from the store; callers that must not wait
 * forever put their own timeout around it.
 */
interface ImportSettingsState {
  previewLoading: boolean;
  preview: ImportSettingsPreview | null;
  previewError: string | null;

  applying: boolean;
  results: ImportSettingsApplyResponse | null;
  applyError: string | null;

  loadPreview: (
    siteId: string | number,
    cameraIds: number[] | undefined,
    settings: string[] | undefined,
    navigate: NavigateFunction
  ) => Promise<ImportSettingsPreview | null>;

  apply: (
    siteId: string | number,
    cameraIds: number[],
    settings: string[],
    overwrite: boolean,
    navigate: NavigateFunction
  ) => Promise<ImportSettingsApplyResponse | null>;

  reset: () => void;
}

const PREVIEW_FAILED = "Couldn't read settings from the VMS.";
const APPLY_FAILED = "Couldn't import settings from the VMS.";

export const useImportSettingsStore = create<ImportSettingsState>(
  (set, get) => ({
    previewLoading: false,
    preview: null,
    previewError: null,

    applying: false,
    results: null,
    applyError: null,

    loadPreview: async (siteId, cameraIds, settings, navigate) => {
      set({ previewLoading: true, preview: null, previewError: null });
      const retryFunction = () => {
        void get().loadPreview(siteId, cameraIds, settings, navigate);
      };
      try {
        const preview = await previewImportSettings(
          siteId,
          cameraIds,
          settings
        );
        set({ preview, previewLoading: false });
        return preview;
      } catch (reason) {
        logger.error("import_settings preview failed", reason);
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          (context) => {
            if (context?.willRetry) return;
            set({ previewError: PREVIEW_FAILED, previewLoading: false });
          }
        );
        return null;
      }
    },

    apply: async (siteId, cameraIds, settings, overwrite, navigate) => {
      set({ applying: true, results: null, applyError: null });
      const retryFunction = () => {
        void get().apply(siteId, cameraIds, settings, overwrite, navigate);
      };
      try {
        const results = await applyImportSettings(
          siteId,
          cameraIds,
          settings,
          overwrite
        );
        set({ results, applying: false });
        return results;
      } catch (reason) {
        logger.error("import_settings apply failed", reason);
        universalAPIErrorHandler(
          reason as AxiosError,
          navigate,
          retryFunction,
          (context) => {
            if (context?.willRetry) return;
            set({ applyError: APPLY_FAILED, applying: false });
          }
        );
        return null;
      }
    },

    reset: () =>
      set({
        previewLoading: false,
        preview: null,
        previewError: null,
        applying: false,
        results: null,
        applyError: null,
      }),
  })
);
